
import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Search, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SelectedIngredients from "./SelectedIngredients";
import { INGREDIENT_CATEGORIES } from "./ingredientData";
import IngredientCategory from "./IngredientCategory";

const formSchema = z.object({
  searchQuery: z.string().optional(),
});

interface SearchFormProps {
  onSearch: (query: string, ingredients: string[], excludedIngredients: string[]) => void;
  isLoading: boolean;
  initialQuery?: string;
  collapsedByDefault?: boolean;
}

const SearchForm = ({ onSearch, isLoading, initialQuery = "", collapsedByDefault = false }: SearchFormProps) => {
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [excludedIngredients, setExcludedIngredients] = useState<string[]>([]);
  const [isIngredientsOpen, setIsIngredientsOpen] = useState(!collapsedByDefault);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      searchQuery: initialQuery || "",
    },
  });

  useEffect(() => {
    if (initialQuery) {
      form.setValue("searchQuery", initialQuery);
    }
  }, [initialQuery, form]);

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    let query = values.searchQuery || "";
    onSearch(query, ingredients, excludedIngredients);
  };

  const toggleIngredient = (ingredient: string) => {
    if (ingredients.includes(ingredient)) {
      setIngredients(ingredients.filter((i) => i !== ingredient));
    } else {
      // If it's in the excluded list, remove it from there first
      if (excludedIngredients.includes(ingredient)) {
        setExcludedIngredients(excludedIngredients.filter((i) => i !== ingredient));
      }
      setIngredients([...ingredients, ingredient]);
    }
  };

  const toggleExcludedIngredient = (ingredient: string) => {
    if (excludedIngredients.includes(ingredient)) {
      setExcludedIngredients(excludedIngredients.filter((i) => i !== ingredient));
    } else {
      // If it's in the included list, remove it from there first
      if (ingredients.includes(ingredient)) {
        setIngredients(ingredients.filter((i) => i !== ingredient));
      }
      setExcludedIngredients([...excludedIngredients, ingredient]);
    }
  };

  const removeIngredient = (ingredient: string) => {
    setIngredients(ingredients.filter((i) => i !== ingredient));
  };

  const removeExcludedIngredient = (ingredient: string) => {
    setExcludedIngredients(excludedIngredients.filter((i) => i !== ingredient));
  };

  return (
    <Card>
      <CardHeader className="pb-0" />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="searchQuery"
              render={({ field }) => (
                <FormItem className="pt-2">
                  <FormControl>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search for recipes..."
                        className="pl-9"
                        {...field}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </>
              )}
            </Button>

            {/* Selected Ingredients */}
            <SelectedIngredients
              ingredients={ingredients}
              onRemove={removeIngredient}
              title="Selected ingredients"
              variant="secondary"
            />

            {/* Selected Excluded Ingredients */}
            <SelectedIngredients
              ingredients={excludedIngredients}
              onRemove={removeExcludedIngredient}
              title="Excluded ingredients"
              variant="destructive"
            />

            {/* Collapsible Ingredients Section */}
            <Collapsible open={isIngredientsOpen} onOpenChange={setIsIngredientsOpen}>
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-between p-2 h-auto text-left font-normal"
                  type="button"
                >
                  <span className="text-sm font-medium">
                    {isIngredientsOpen ? 'Hide Ingredients' : 'Show Ingredients'}
                  </span>
                  {isIngredientsOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="space-y-4">
                {/* Instructions banner */}
                <div className="bg-muted px-3 py-2 rounded-md text-sm text-muted-foreground flex items-center justify-center">
                  <span>
                    <strong>Tip:</strong> Click to add ingredient, right-click to exclude
                  </span>
                </div>

                {/* Categorized ingredients */}
                <div className="border rounded-md p-2">
                  {INGREDIENT_CATEGORIES.map((category, index) => (
                    <IngredientCategory
                      key={category.name}
                      category={category}
                      ingredients={ingredients}
                      excludedIngredients={excludedIngredients}
                      toggleIngredient={toggleIngredient}
                      toggleExcludedIngredient={toggleExcludedIngredient}
                      index={index}
                      totalCategories={INGREDIENT_CATEGORIES.length}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </form>
      </Form>
    </Card>
  );
};

export default SearchForm;
